/**
 * InlineComboboxField — Text input with dropdown suggestions.
 * Allows selection from list OR custom entry.
 *
 * @example
 * <InlineComboboxField
 *   value={project.generatorMake}
 *   options={[...GENERATOR_MAKES]}
 *   placeholder="Generator make"
 *   onSave={(v) => saveField('generatorMake', v)}
 * />
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';

interface InlineComboboxFieldProps {
  value?: string;
  options: readonly string[] | string[];
  placeholder?: string;
  onSave: (value: string) => Promise<void>;
  icon?: LucideIcon;
  disabled?: boolean;
  allowCustom?: boolean;
}

export function InlineComboboxField({
  value,
  options,
  placeholder = '',
  onSave,
  icon: Icon,
  disabled,
  allowCustom = true,
}: InlineComboboxFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = inputValue
    ? options.filter((o) => o.toLowerCase().includes(inputValue.toLowerCase()))
    : [...options];

  // Sync inputValue when external value prop changes (e.g., after save or data refresh)
  useEffect(() => {
    if (!isEditing) {
      setInputValue(value || '');
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (highlightIndex >= 0 && dropdownRef.current) {
      const el = dropdownRef.current.children[highlightIndex] as HTMLElement;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  useEffect(() => {
    if (!isEditing) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsEditing(false);
        setShowDropdown(false);
        setInputValue(value || '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isEditing, value]);

  const startEdit = () => {
    if (disabled) return;
    setInputValue(value || '');
    setHighlightIndex(-1);
    setIsEditing(true);
    setShowDropdown(true);
  };

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setShowDropdown(false);
    setInputValue(value || '');
  }, [value]);

  const saveValue = useCallback(async (v: string) => {
    const trimmed = v.trim();
    if (trimmed === (value || '')) {
      cancelEdit();
      return;
    }
    if (!allowCustom && !options.includes(trimmed)) {
      toast.error('Please select a value from the list');
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
      setShowDropdown(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [value, allowCustom, options, onSave, cancelEdit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      cancelEdit();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filtered.length) {
        saveValue(filtered[highlightIndex]);
      } else {
        saveValue(inputValue);
      }
    } else if (e.key === 'Tab') {
      if (filtered.length > 0 && highlightIndex >= 0) {
        e.preventDefault();
        saveValue(filtered[highlightIndex]);
      } else if (filtered.length === 1) {
        e.preventDefault();
        saveValue(filtered[0]);
      }
    }
  };

  if (!isEditing) {
    return (
      <div
        className="flex items-center gap-2 cursor-pointer group min-h-[28px]"
        onClick={startEdit}
      >
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
        <span className={`text-sm ${value ? 'text-slate-900' : 'text-slate-400'} group-hover:text-sky-600`}>
          {value || placeholder}
        </span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative flex items-center gap-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
      <div className="relative flex-1">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setHighlightIndex(-1);
            setShowDropdown(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className="w-full border border-sky-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none disabled:opacity-50"
          placeholder={placeholder}
          role="combobox"
          aria-expanded={showDropdown && filtered.length > 0}
          aria-activedescendant={highlightIndex >= 0 ? `combobox-option-${highlightIndex}` : undefined}
        />
        {showDropdown && filtered.length > 0 && (
          <ul
            ref={dropdownRef}
            className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
            role="listbox"
          >
            {filtered.map((opt, i) => (
              <li
                key={opt}
                id={`combobox-option-${i}`}
                role="option"
                aria-selected={i === highlightIndex}
                className={`px-3 py-1.5 text-sm cursor-pointer ${
                  i === highlightIndex ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveValue(opt);
                }}
                onMouseEnter={() => setHighlightIndex(i)}
              >
                {opt}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
